import type { DataProvenance } from '@console/types'
import styles from './DataProvenanceLine.module.css'

export interface DataProvenanceLineProps {
  provenance: DataProvenance
  /** The tenant the operator is signed in to, for naming both sides of a mismatch. */
  sessionTenantName: string
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id
}

/**
 * Says whose data is on the screen, and how sure the console is.
 *
 * The console's header names the *signed-in* tenant. Nothing in a Cadence
 * response names the tenant the data belongs to — all 19 v1 routes were
 * enumerated and none carries one — so a console pointed at a Cadence serving
 * a different tenant renders a correct-looking screen under the wrong name and
 * cannot tell. This line is the console declining to make that claim silently.
 *
 * It follows `DegradationBanner`'s discipline of rendering nothing when there
 * is nothing to say, but the bar is higher: silence here means the tenant was
 * *reported by Cadence* and matched. Configuration is never silent, because
 * configuration is not verification — the moment this renders nothing on a
 * `configured` source, the whole component is decoration.
 */
export function DataProvenanceLine({ provenance, sessionTenantName }: DataProvenanceLineProps) {
  const { host, tenantId, tenantLabel, source, matchesSession } = provenance

  if (source === 'reported' && matchesSession === true) return null

  // Monospace suits a raw identifier and actively hurts a human name, whose
  // word spacing goes ragged in a fixed-pitch face. So the class follows
  // *which* of the two is being shown, not the slot it sits in.
  const name = tenantLabel ?? (tenantId === null ? null : shortId(tenantId))
  const nameClass = tenantLabel === null ? styles['tenant'] : undefined

  if (matchesSession === false) {
    return (
      <div className={styles['alert']} role="alert">
        <p>
          The data on this screen belongs to tenant <strong className={nameClass}>{name}</strong>,
          served by Cadence at {host}. You are signed in to <strong>{sessionTenantName}</strong>.
          These are different tenants.
        </p>
        <p className={styles['caveat']}>
          {source === 'configured'
            ? 'Read from this console’s configuration — Cadence does not report which tenant it serves, so the console cannot verify either side.'
            : 'Reported by Cadence.'}
        </p>
      </div>
    )
  }

  if (source === 'unknown') {
    return (
      <p className={styles['quiet']} role="status">
        Showing data from Cadence at {host}. Which tenant it belongs to is not recorded, so the
        console cannot confirm it is {sessionTenantName}’s.
      </p>
    )
  }

  return (
    <p className={styles['quiet']} role="status">
      Showing data from Cadence at {host} for tenant <span className={nameClass}>{name}</span> —
      matches your account, but taken from configuration: Cadence does not report which tenant it
      serves.
    </p>
  )
}
